import { Box, Flex, HStack, Image, Text } from "@chakra-ui/react";
import { FeedCardHalo } from "./FeedCardHalo";
import { FeedComicRecommendationItem, FeedGenreDiveItem } from "./types";
import { useFeedExpand } from "./feedExpandContext";

interface FeedCompactComicCardProps {
  // genre_dive shares the rail shape; accept both so this card is the
  // canonical "comics rail" presentation across providers.
  item: FeedComicRecommendationItem | FeedGenreDiveItem;
  isActive: boolean;
}

/**
 * "You might like" / "More <facet>" rail. Image-forward: rail title across
 * the top, then a row of large covers filling the rest of the card. Each
 * cover is a tap target to its comic. The halo color comes from the first
 * cover so the rail's glow matches its lead comic.
 */
export function FeedCompactComicCard({
  item,
  isActive,
}: FeedCompactComicCardProps) {
  const haloSrc =
    item.comics[0]?.cover_url ?? item.comics[0]?.cover_small_url ?? null;
  const { open: openExpand } = useFeedExpand();
  return (
    <Box position="relative" w="100%" h="100%">
      <FeedCardHalo src={haloSrc} />
    <Flex
      direction="column"
      w="100%"
      h="100%"
      position="relative"
      zIndex={1}
      bgGradient="linear(135deg, rgba(46, 28, 70, 0.95) 0%, rgba(18, 16, 28, 0.95) 100%)"
      borderRadius="2xl"
      border="1px solid rgba(255,255,255,0.06)"
      overflow="hidden"
      color="white"
    >
      {/* Inline rail tag removed — the SuggestionFeed renders a section
          header above the card that carries item.rail_title. */}
      <HStack spacing={2} px={3} py={3} flex="1" minH="0" align="stretch">
        {item.comics.map((comic) => (
          <Box
            as="a"
            key={comic.comic_id}
            href={comic.url}
            // Tap → expand fullscreen preview. The href stays so right-click
            // / long-press / open-in-new-tab still goes to the comic page;
            // primary-click is intercepted in favor of the inline preview.
            // Pass release_uuid + reading_direction directly when the
            // backend resolved them so the preview skips the slug round-trip.
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
                return;
              }
              e.preventDefault();
              openExpand({
                slug: comic.slug,
                releaseUuid: comic.release_uuid,
                readingDirection: comic.reading_direction,
              });
            }}
            tabIndex={isActive ? 0 : -1}
            position="relative"
            flex="1"
            minW="0"
            borderRadius="md"
            overflow="hidden"
            bg="black"
            cursor="pointer"
            _hover={{ filter: "brightness(1.1)" }}
            transition="filter 120ms ease"
          >
            {comic.cover_url ?? comic.cover_small_url ? (
              <Image
                src={comic.cover_url ?? comic.cover_small_url ?? ""}
                alt={comic.name}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            ) : (
              <Box
                w="100%"
                h="100%"
                bgGradient="linear(135deg, orange.500 0%, purple.700 100%)"
              />
            )}
            <Box
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              h="55%"
              bgGradient="linear(to-t, rgba(0,0,0,0.9) 0%, transparent 100%)"
              pointerEvents="none"
            />
            <Box position="absolute" left={2} right={2} bottom={2}>
              <Text
                fontSize="xs"
                fontWeight="bold"
                noOfLines={2}
                lineHeight="1.15"
                sx={{ textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}
              >
                {comic.name}
              </Text>
              {comic.creator_name ? (
                <Text fontSize="2xs" opacity={0.75} noOfLines={1}>
                  {comic.creator_name}
                </Text>
              ) : null}
            </Box>
          </Box>
        ))}
      </HStack>
    </Flex>
    </Box>
  );
}
